// CLI `grid fork <id|org/slug>` — the Case-3 verb: "I saw something cool,
// make it mine."
//
// Spec: docs/decisions/036-versioned-entities-and-global-addressability.md §V4
// and docs/specs/2026-06-07-versioned-entities-and-global-addressability.md §5.
// Closes backlog 412.
//
// Fork mints a NEW owned entity (a new `entity_id`) in the caller's chosen
// destination org, with lineage back to the source recorded permanently
// (`forked_from` + `forked_from_version_id`). The fork's first version
// references the source version's source bytes copy-on-write — no second
// upload. Nothing is written to the local directory: the next step is
// `grid clone <new-id>` to pull the source down, or `grid plug` once
// you have a working copy.
//
// This is distinct from `clone`: `clone` is a *takeover* of an entity the
// caller can already address — it binds the local dir to the SAME `entity_id`.
// `fork` mints a new node owned by the caller. See decision 012 and ADR 036.

import { Command } from 'commander';

import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { handleV2Error } from '../handle-error.js';
import { V2ApiError } from '../api/v2-errors.js';
import { getActiveOrg, notSignedInMessage } from '../active-org.js';
import { CloudGridError } from '../utils.js';

/** Parsed fork target: the source ref the server resolves, plus the source
 *  org when the user addressed it org-qualified. */
export interface ForkTarget {
  /** Slug or entity_id of the source entity. */
  idOrSlug: string;
  /** Source org, when derivable from a URL or `org/slug` form. */
  sourceOrg?: string;
}

/**
 * Parse the URL / slug / `org/slug` the user typed into a source ref. Pure —
 * exported for unit tests.
 *
 * Accepted forms:
 *   - https://atomic.cloudgrid.io/cool-thing   → { idOrSlug: 'cool-thing', sourceOrg: 'atomic' }
 *   - https://cool-thing.atomic.cloudgrid.io   → { idOrSlug: 'cool-thing', sourceOrg: 'atomic' }
 *   - atomic/cool-thing                        → { idOrSlug: 'cool-thing', sourceOrg: 'atomic' }
 *   - cool-thing                               → { idOrSlug: 'cool-thing' }
 *   - 1a2b3c4d-…                               → { idOrSlug: '1a2b3c4d-…' }
 */
export function parseForkTarget(input: string): ForkTarget {
  const raw = input.trim();
  if (raw.length === 0) {
    throw new CloudGridError('Provide an entity to fork: grid fork <id|org/slug>');
  }

  if (raw.includes('://')) {
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      throw new CloudGridError(`Not a valid URL: ${raw}`);
    }
    const hostLabels = url.hostname.split('.').filter(Boolean);
    const pathSegs = url.pathname.split('/').filter(Boolean);
    if (pathSegs.length > 0) {
      // Path-style: https://<org>.cloudgrid.io/<slug>
      const idOrSlug = pathSegs[pathSegs.length - 1];
      const sourceOrg =
        hostLabels.length >= 3 && hostLabels[0] !== 'www' ? hostLabels[0] : undefined;
      return sourceOrg ? { idOrSlug, sourceOrg } : { idOrSlug };
    }
    // Subdomain-style: https://<slug>.<org>.cloudgrid.io
    if (hostLabels.length >= 3) {
      return { idOrSlug: hostLabels[0], sourceOrg: hostLabels[1] };
    }
    throw new CloudGridError(
      `Couldn't read an entity slug from ${raw}. Pass the slug directly: grid fork <slug>`,
    );
  }

  // `org/slug` form (exactly one slash, no scheme).
  const parts = raw.split('/').filter(Boolean);
  if (raw.includes('/') && parts.length === 2) {
    return { idOrSlug: parts[1], sourceOrg: parts[0] };
  }
  return { idOrSlug: raw };
}

/**
 * The path segment the runtime fork endpoint resolves the source from. A
 * UUID / bare slug passes through; an org-qualified ref becomes the
 * `org/slug` form `requireEntityAccess` accepts as ONE encoded segment.
 */
export function forkSourceRef(target: ForkTarget): string {
  return target.sourceOrg ? `${target.sourceOrg}/${target.idOrSlug}` : target.idOrSlug;
}

/**
 * Runtime fork endpoint response shape (ADR 036 §5.2). The inspiration fork
 * (Decision 044 P1.3) returns the standard inspiration response, so the fields
 * this summary reads (`entity_id`, `name`, `kind`, `org`) are typed loosely
 * enough to cover both shapes; the lineage fields are runtime-only and absent
 * on the inspiration path.
 */
interface ForkResponse {
  entity_id: string;
  name: string;
  kind: 'app' | 'agent' | 'inspiration';
  org: { org_id?: string; slug?: string | null };
  forked_from?: string | null;
  forked_from_version_id?: string | null;
  current_version_id?: string | null;
}

export interface RunForkOpts {
  client: IV2Client;
  /** The URL / slug / org/slug the user typed for the source. */
  target: string;
  /** `--into-org <slug>` — destination org (required for multi-org callers). */
  intoOrg?: string;
  /** `--name <slug>` — override the new entity's slug. */
  name?: string;
  /** `--version <id>` — fork an older version of the source. */
  version?: string;
  /** `--json` — print the raw response instead of the human summary. */
  json?: boolean;
}

export async function runFork(opts: RunForkOpts): Promise<void> {
  const target = parseForkTarget(opts.target ?? '');
  const sourceRef = forkSourceRef(target);

  // Resolve the destination org server-side so a stale local slug never
  // decides where the fork lands. `--into-org` overrides the active org; the
  // resolver throws a friendly listing when nothing resolves or the slug is
  // not a membership.
  const intoOrg = await getActiveOrg(opts.client, { override: opts.intoOrg });
  const client = opts.client.withOrgSlug(intoOrg.slug);

  // Decision 044 §4 — one verb, kind-aware routing. A runtime forks at
  // `/runtimes/:id/fork`; an inspiration forks at `/inspirations/:id/fork`
  // (Decision 044 P1.3, #1448). The CLI does not know the target's kind up
  // front, so it tries the runtime route first and, when the server reports
  // the target is an inspiration (`400 NOT_A_RUNTIME`, emitted by
  // `requireEntityAccess()` before the runtime fork handler runs), retries
  // against the inspiration route. Every other error is surfaced as-is.
  const runtimeBody: { into_org_slug: string; name?: string; source_version_id?: string } = {
    into_org_slug: intoOrg.slug,
  };
  if (opts.name) runtimeBody.name = opts.name;
  if (opts.version) runtimeBody.source_version_id = opts.version;

  let resp: ForkResponse;
  try {
    resp = await client.post<ForkResponse>(
      `/api/v2/runtimes/${encodeURIComponent(sourceRef)}/fork`,
      runtimeBody,
    );
  } catch (err) {
    if (err instanceof V2ApiError && err.code === 'NOT_A_RUNTIME') {
      // The target is an inspiration — fork it on the inspiration route. The
      // inspiration remix always lands in the caller's active grid, which the
      // client is already bound to (via `--into-org`/active-org resolution
      // above), so no `into_org_slug` travels in the body. `source_version_id`
      // has no meaning for inspirations (no version history); a `--version`
      // passed here is silently dropped rather than sent to an endpoint that
      // would reject it.
      const inspirationBody: { name?: string } = {};
      if (opts.name) inspirationBody.name = opts.name;
      try {
        resp = await client.post<ForkResponse>(
          `/api/v2/inspirations/${encodeURIComponent(sourceRef)}/fork`,
          inspirationBody,
        );
      } catch (inspErr) {
        if (inspErr instanceof V2ApiError) throw mapForkError(inspErr, sourceRef);
        throw inspErr;
      }
    } else if (err instanceof V2ApiError) {
      throw mapForkError(err, sourceRef);
    } else {
      throw err;
    }
  }

  if (opts.json) {
    process.stdout.write(JSON.stringify(resp) + '\n');
    return;
  }

  const orgSlug = resp.org?.slug ?? intoOrg.slug;
  const lines = [
    `Forked ${sourceRef} into ${orgSlug} as ${resp.name} (${resp.entity_id}).`,
  ];
  if (resp.forked_from_version_id) {
    lines.push(`  From version ${resp.forked_from_version_id}.`);
  }
  // Inspirations are served artifacts, not deployable working trees — there is
  // nothing to clone down. Only the runtime fork suggests a clone as the next
  // step; the inspiration remix is already live at its own URL.
  if (resp.kind !== 'inspiration') {
    lines.push(`  Next: grid clone ${resp.entity_id}`);
  }
  process.stdout.write(lines.join('\n') + '\n');
}

/** Map fork-endpoint errors to plain, actionable messages (§23 voice). */
function mapForkError(err: V2ApiError, sourceRef: string): CloudGridError {
  if (
    err.code === 'AUTH_REQUIRED' ||
    err.code === 'AUTH_EXPIRED' ||
    err.code === 'AUTH_INVALID'
  ) {
    return new CloudGridError(notSignedInMessage());
  }
  if (err.code === 'INTO_ORG_REQUIRED') {
    return new CloudGridError(
      'You belong to more than one grid. Pass --into-org <slug> to choose where the fork lands.',
    );
  }
  if (err.code === 'FORBIDDEN_ROLE') {
    return new CloudGridError(
      'Forking into that grid needs admin or builder role there.',
    );
  }
  if (err.code === 'NOT_AUTHORIZED' || err.status === 403) {
    return new CloudGridError(
      `You can't fork '${sourceRef}' into that grid. Check you're a member of both the source's grid and the destination.`,
    );
  }
  if (err.code === 'CROSS_ORG_FORK_FORBIDDEN') {
    // Decision 044 P1.3 — an inspiration remix across the grid boundary needs
    // the source to be publicly forkable (link-visible, a system template, or
    // owner-opted `forkable: 'public'`).
    return new CloudGridError(
      `'${sourceRef}' is not publicly forkable, so it can only be remixed within its home grid.`,
    );
  }
  if (err.code === 'CROSS_ORG_RUNTIME_FORK_FORBIDDEN') {
    // ADR 036 §6.1 — a runtime fork must land in the source's home org. The
    // server attaches the source org slug in the error detail; fall back to a
    // neutral phrasing if it is absent.
    const srcSlug = err.details?.find((d) => typeof d.source_org_slug === 'string')?.source_org_slug;
    const orgClause = srcSlug ? ` ('${srcSlug}')` : '';
    return new CloudGridError(
      `Runtime forks must land in the source's home grid${orgClause}. The --into-org\n` +
        `flag only applies to system templates and inspirations. Copying this app\n` +
        `into another grid with the lineage intact is not supported by design; to make\n` +
        `an independent copy without lineage, use:\n\n` +
        `  grid clone ${sourceRef} ./my-copy\n` +
        `  cd my-copy && grid new my-app`,
    );
  }
  if (err.code === 'NO_SOURCE_VERSION') {
    return new CloudGridError(
      `'${sourceRef}' has no deployed version to fork yet. Deploy it first, then fork.`,
    );
  }
  if (err.code === 'SOURCE_RETIRED') {
    return new CloudGridError(
      `The version of '${sourceRef}' you asked for is retained for history but its source was reclaimed. Fork HEAD or a more recent version.`,
    );
  }
  if (err.code === 'VERSION_NOT_FOUND') {
    return new CloudGridError(`No version with that id on '${sourceRef}'.`);
  }
  if (err.code === 'CONFLICT') {
    return new CloudGridError(
      'An entity with that name already exists in the destination grid. Pass a different --name, or omit it for an auto-suffixed one.',
    );
  }
  if (err.code === 'ORG_NOT_FOUND') {
    return new CloudGridError(err.message);
  }
  if (err.status === 404 || err.code.endsWith('_NOT_FOUND') || err.code === 'NOT_FOUND') {
    return new CloudGridError(`No entity matched '${sourceRef}'. Check the id or org/slug.`);
  }
  if (err.code === 'VALIDATION_FAILED') {
    return new CloudGridError(err.message);
  }
  return new CloudGridError(
    `Server error (${err.status} ${err.code}). Try again, or run grid feedback if it persists.`,
  );
}

export const forkCommand: Command = new Command('fork')
  .description(
    'Copy an entity into your grid as a new node with lineage back to the source (distinct from clone, which takes over the same entity).',
  )
  .argument('<id|org/slug>', 'Entity URL, id, or org/slug to fork')
  .option('--into-org <slug>', 'Destination grid for the fork (required if you belong to more than one grid)')
  .option('--name <slug>', 'Override the new entity name (defaults to the source name plus a suffix)')
  .option('--version <id>', 'Fork an older version of the source instead of HEAD')
  .option('--json', 'Print the result as JSON')
  .action(
    async (
      idOrSlug: string,
      opts: { intoOrg?: string; name?: string; version?: string; json?: boolean },
    ) => {
      try {
        const client = await getV2Client(opts.intoOrg);
        await runFork({
          client,
          target: idOrSlug,
          intoOrg: opts.intoOrg,
          name: opts.name,
          version: opts.version,
          json: opts.json,
        });
      } catch (err) {
        await handleV2Error(err);
      }
    },
  );
